import { differenceInCalendarDays } from 'date-fns';
import type { ReactNode } from 'react';
import type { IntlShape } from 'react-intl';
import { FormattedMessage, FormattedNumber } from 'react-intl';

import { ProductType } from '@xing-com/crate-common-graphql-types';

import type { PromotedCardProps } from '../components/product-cards/components/promoted-card';
import type { ProductCardsData } from '../components/product-cards/product-cards.types';

import type { CampaignCardData } from './map-campaign-to-card-data';

type CampaignCards = {
  premium: CampaignCardData | null;
  projobs: CampaignCardData | null;
};

type CardCallout = PromotedCardProps['callout'];
type MembershipCard = Omit<
  PromotedCardProps,
  'activationUrl' | 'callout' | 'detailsUrl' | 'onDiscover' | 'perks'
> & { product: ProductType };

const basicCard = ({
  formatMessage,
}: Pick<IntlShape, 'formatMessage'>): ProductCardsData['basic'] => ({
  title: formatMessage({
    id: 'UPSELL_MEMBERSHIPS_BASIC_CARD_TITLE',
  }),
  status: formatMessage({
    id: 'UPSELL_MEMBERSHIPS_BASIC_CARD_STATUS',
  }),
  benefitsTitle: formatMessage({
    id: 'UPSELL_MEMBERSHIPS_BASIC_CARD_INFO',
  }),
  benefits: [
    formatMessage({
      id: 'UPSELL_MEMBERSHIPS_BASIC_CARD_BENEFIT_1',
    }),
    formatMessage({
      id: 'UPSELL_MEMBERSHIPS_BASIC_CARD_BENEFIT_2',
    }),
    formatMessage({
      id: 'UPSELL_MEMBERSHIPS_BASIC_CARD_BENEFIT_3',
    }),
  ],
});

const priceNode = (
  campaign: CampaignCardData,
  { formatMessage }: Pick<IntlShape, 'formatMessage'>
): ReactNode => (
  <>
    {formatMessage({
      id: 'UPSELL_MEMBERSHIPS_CARD_PRICE_PREFIX',
    })}{' '}
    <strong>
      <FormattedNumber
        value={campaign.price.initial.monthly}
        style="currency"
        currency={campaign.price.currency}
      />
    </strong>{' '}
    {formatMessage({
      id: 'UPSELL_MEMBERSHIPS_CARD_PRICE_SUFFIX',
    })}
  </>
);

const previousPriceNode = (campaign: CampaignCardData): ReactNode =>
  campaign.price.discount > 0 ? (
    <FormattedMessage
      id="UPSELL_PURCHASE_OFFER_BOX_RUNTIME_RENEWAL_PRICE"
      values={{
        renewalPrice: (
          <s>
            <FormattedNumber
              value={
                campaign.price.current === null || campaign.price.current === 0
                  ? campaign.price.renewal.monthly
                  : campaign.price.current
              }
              style="currency"
              currency={campaign.price.currency}
            />
          </s>
        ),
      }}
    />
  ) : null;

const callout = (
  campaign: CampaignCardData,
  {
    formatMessage,
    formatNumber,
  }: Pick<IntlShape, 'formatMessage' | 'formatNumber'>
): CardCallout => {
  if (campaign.price.discount <= 0) {
    return { variant: 'default' };
  }

  const value = `${formatNumber(campaign.price.discount * 100, {
    maximumFractionDigits: 0,
  })}%`;

  if (!campaign.endsAt) {
    return {
      eyebrow: formatMessage({
        id: 'UPSELL_MEMBERSHIPS_CARD_DISCOUNT_PREFIX',
      }),
      value,
      label: formatMessage({
        id: 'UPSELL_MEMBERSHIPS_CARD_DISCOUNT_SUFFIX',
      }),
      variant: 'discount',
    } as const;
  }

  return {
    countdown: formatMessage(
      {
        id: 'UPSELL_PURCHASE_COUNTDOWN_BUBBLE_MESSAGE',
      },
      {
        span: (chunks) => chunks.join(''),
        remainingDays: differenceInCalendarDays(
          new Date(campaign.endsAt),
          new Date()
        ),
      }
    ),
    eyebrow: formatMessage({
      id: 'UPSELL_MEMBERSHIPS_CARD_DISCOUNT_PREFIX',
    }),
    value,
    label: formatMessage({
      id: 'UPSELL_MEMBERSHIPS_CARD_DISCOUNT_SUFFIX',
    }),
    variant: 'countdown',
  } as const;
};

const unpromotedMarker = (
  campaign: CampaignCardData,
  {
    formatMessage,
    formatNumber,
  }: Pick<IntlShape, 'formatMessage' | 'formatNumber'>
): string | undefined => {
  if (campaign.price.discount <= 0) {
    return undefined;
  }

  return `${formatMessage({
    id: 'UPSELL_MEMBERSHIPS_CARD_DISCOUNT_PREFIX',
  })} ${formatNumber(campaign.price.discount * 100, {
    maximumFractionDigits: 0,
  })}% ${formatMessage({
    id: 'UPSELL_MEMBERSHIPS_CARD_DISCOUNT_SUFFIX',
  })}`;
};

const membershipCard = (
  campaign: CampaignCardData,
  { formatMessage }: Pick<IntlShape, 'formatMessage'>
): MembershipCard => ({
  product: campaign.product,
  title: formatMessage({
    id:
      campaign.product === ProductType.Premium
        ? 'UPSELL_PURCHASE_OFFER_BOX_HEADLINE'
        : 'UPSELL_PURCHASE_OFFER_BOX_HEADLINE_PROJOBS',
  }),
  price: priceNode(campaign, { formatMessage }),
  previousPrice: previousPriceNode(campaign),
  action: formatMessage({
    id:
      campaign.product === ProductType.Premium
        ? 'UPSELL_MEMBERSHIPS_PREMIUM_CTA'
        : 'UPSELL_MEMBERSHIPS_PROJOBS_CTA',
  }),
  cancellation: formatMessage({
    id: 'UPSELL_MEMBERSHIPS_CANCELLATION_INFO',
  }),
  benefitsTitle: campaign.textValues.subheading,
  benefits: campaign.textValues.benefits,
  discover: formatMessage({
    id:
      campaign.product === ProductType.Premium
        ? 'UPSELL_MEMBERSHIPS_PREMIUM_BENEFITS_CTA'
        : 'UPSELL_MEMBERSHIPS_PROJOBS_BENEFITS_CTA',
  }),
});

export const createProductCardsData = (
  product: ProductType,
  campaigns: CampaignCards | undefined,
  formatters: Pick<IntlShape, 'formatMessage' | 'formatNumber'>
): ProductCardsData | null => {
  const premium = campaigns?.premium;
  const projobs = campaigns?.projobs;

  const promoted = product === ProductType.Premium ? premium : projobs;

  if (!promoted) {
    return null;
  }

  const unpromoted = product === ProductType.Premium ? projobs : premium;

  return {
    basic: basicCard(formatters),
    promoted: {
      ...membershipCard(promoted, formatters),
      callout: callout(promoted, formatters),
    },
    ...(unpromoted
      ? {
          unpromoted: {
            ...membershipCard(unpromoted, formatters),
            marker: unpromotedMarker(unpromoted, formatters),
          },
        }
      : {}),
  };
};
